import { useQuery } from '@apollo/client';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import {
  Button,
  Field,
  FluentProvider,
  Input,
  Textarea,
  webLightTheme,
  makeStyles,
} from '@fluentui/react-components';
import { AddRegular, DismissRegular } from '@fluentui/react-icons';
import React, { CSSProperties, useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import momentUtc from '../../utils/DateFormatter';
import {
  GET_FILLING_FREQUENCIES,
  GET_FILING_PREPARATION_COMPANIES,
  GET_TAX_TYPES,
} from '../../utils/statApi/CountryApi';
import { ALL_PROCESS_TYPE_LIST_QUERY } from '../../utils/statApi/ProcessTypesApi';
import IDropdownReference from '../../utils/types/IDropdownReference';
import IFieldsetProps from '../../utils/types/IFieldsetProps';
import MonthDayPicker from '../common/formFields/monthDayPicker/MonthDayPicker';
import QueryBasedDropdown from '../common/formFields/queryBasedDropdown/QueryBasedDropdown';
import StackColumn from '../common/StackColumn';
import UtcDatePicker from '../common/utcDatePicker/UtcDatePicker';
import { IComplianceRequirement, ITaxRate } from './CountryConfiguration.config';
import { iconButtonStyles } from '../../app/common/styles/CommonStyleObjects';

const FilingTypeDropdown = (props: IFieldsetProps): JSX.Element => {
  const { value, label, errors, handleChange, valueKey, outputKey } = props;
  const { loading, data } = useQuery(ALL_PROCESS_TYPE_LIST_QUERY);
  const { allProcessTypes } = !loading && data ? data : [];

  return (
    <QueryBasedDropdown
      value={value as IDropdownReference}
      label={label}
      outputKey={outputKey}
      dropdownOptionsData={allProcessTypes}
      errors={errors}
      handleChange={handleChange}
      valueKey={valueKey}
      required
    />
  );
};

const FilingFrequencyDropdown = (props: IFieldsetProps): JSX.Element => {
  const { value, label, errors, handleChange, valueKey, outputKey } = props;
  const { loading, data } = useQuery(GET_FILLING_FREQUENCIES);
  const { filingFrequencies } = !loading && data ? data : [];

  return (
    <QueryBasedDropdown
      value={value as IDropdownReference}
      label={label}
      outputKey={outputKey}
      dropdownOptionsData={filingFrequencies}
      errors={errors}
      handleChange={handleChange}
      valueKey={valueKey}
      required
    />
  );
};

const FilingPreparationCompanyDropdown = (props: IFieldsetProps): JSX.Element => {
  const { value, label, errors, handleChange, valueKey, outputKey } = props;
  const { loading, data } = useQuery(GET_FILING_PREPARATION_COMPANIES);
  const { filingPreparationCompanies } = !loading && data ? data : [];

  return (
    <QueryBasedDropdown
      value={value as IDropdownReference}
      label={label}
      outputKey={outputKey}
      dropdownOptionsData={filingPreparationCompanies}
      errors={errors}
      handleChange={handleChange}
      valueKey={valueKey}
      required
    />
  );
};

const TaxTypeDropdown = (props: IFieldsetProps): JSX.Element => {
  const { value, label, errors, errorMessage, handleChange, valueKey, outputKey } = props;
  const { loading, data } = useQuery(GET_TAX_TYPES);
  const { taxTypes } = !loading && data ? data : [];

  return (
    <QueryBasedDropdown
      value={value as IDropdownReference}
      label={label}
      outputKey={outputKey}
      dropdownOptionsData={taxTypes}
      errorMessage={errorMessage}
      errors={errors}
      handleChange={handleChange}
      valueKey={valueKey}
      required
    />
  );
};

interface ComplianceRequirementFormProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
  complianceRequirement: IComplianceRequirement;
}

const useComplianceStyles = makeStyles({
  taxRate: { justifyContent: 'space-between' },
  addTaxRate: { alignSelf: 'center' },
  paddingBottom: { paddingBottom: '15px' },
});

const ComplianceRequirementForm = ({
  form,
  complianceRequirement,
}: ComplianceRequirementFormProps): JSX.Element => {
  const complianceClasses = useComplianceStyles();
  const [newTaxRateId, setNewTaxRateId] = useState(-1);
  const { control, errors, setValue, register, formState, trigger } = form;

  const { fields, insert, remove } = useFieldArray({
    control,
    name: 'taxRates',
    keyName: 'formId',
  });

  const stackStyles: CSSProperties = {
    position: 'relative',
    marginTop: 10,
    padding: 15,
  };

  const createTaxRateComponent = (): void => {
    insert(fields.length, {
      id: newTaxRateId,
      taxType: null,
      description: '',
      rate: null,
      effectiveDate: null,
    });
    setNewTaxRateId(newTaxRateId - 1);
  };

  const buttonStyles: React.CSSProperties = {
    position: 'absolute',
    right: 0,
    zIndex: 1,
    top: 0,
  };

  const taxRateClassName = complianceClasses.taxRate;

  const addTaxRateClassName = complianceClasses.addTaxRate;

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 20 }} className={complianceClasses.paddingBottom}>
        <input type="hidden" ref={register()} name="id" defaultValue={complianceRequirement?.id} />

        <Controller
          label="Requirement Name"
          name="name"
          control={control}
          defaultValue={complianceRequirement?.name || ''}
          rules={{
            required: 'Please enter the requirement name',
            maxLength: {
              value: 255,
              message: 'Requirement Name must be 255 characters or fewer.',
            },
          }}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field label="Requirement Name" required validationMessage={errors?.name?.message}>
              <Input
                id="name"
                name={name}
                value={value}
                onChange={(e, inputData) => onChange(inputData.value)}
              />
            </Field>
          )}
        />

        <MonthDayPicker
          form={form}
          dueDateDay={complianceRequirement?.filingDueDateDay || 1}
          dueDateMonth={complianceRequirement?.filingDueDateMonth || 1}
          dayFormName="filingDueDateDay"
          monthFormName="filingDueDateMonth"
          label="Filing Due Date"
          required
        />

        <Controller
          name="filingType"
          control={control}
          render={({ onChange, onBlur, value, name }): React.ReactElement => (
            <FilingTypeDropdown
              label="Filing Type"
              value={complianceRequirement?.filingType}
              valueKey="filingType"
              handleChange={(newValue: string, valueKey: string): void => {
                setValue('filingType', newValue);
              }}
              errors={errors}
            />
          )}
          defaultValue={complianceRequirement?.filingType || null}
          rules={{
            required: 'Please select a filing type',
          }}
          required
        />

        <Controller
          name="filingFrequency"
          control={control}
          render={({ onChange, onBlur, value, name }): React.ReactElement => (
            <FilingFrequencyDropdown
              label="Filing Frequency"
              value={complianceRequirement?.filingFrequency}
              valueKey="filingFrequency"
              handleChange={(newValue: string, valueKey: string): void => {
                setValue('filingFrequency', newValue);
              }}
              errors={errors}
            />
          )}
          defaultValue={complianceRequirement?.filingFrequency || null}
          rules={{
            required: 'Please select a filing frequency',
          }}
          required
        />

        <Controller
          name="filingPreparationCompany"
          control={control}
          render={({ onChange, onBlur, value, name }): React.ReactElement => (
            <FilingPreparationCompanyDropdown
              label="Filing Preparation Company"
              value={complianceRequirement?.filingPreparationCompany}
              valueKey="filingPreparationCompany"
              handleChange={(newValue: string, valueKey: string): void => {
                setValue('filingPreparationCompany', newValue);
              }}
              errors={errors}
            />
          )}
          defaultValue={complianceRequirement?.filingPreparationCompany || null}
          rules={{
            required: 'Please select a filing preparation company',
          }}
          required
        />

        <Controller
          label="Comments"
          name="comments"
          control={control}
          defaultValue={complianceRequirement?.comments || ''}
          rules={{
            maxLength: {
              value: 250,
              message: 'Input is greater than the maximum character limit of 250.',
            },
          }}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field label="Comments" validationMessage={errors?.comments?.message}>
              <Textarea
                id="comments"
                name={name}
                value={value}
                onChange={(e, inputData) => onChange(inputData.value)}
                resize="none"
              />
            </Field>
          )}
        />

        <Stack horizontal className={taxRateClassName}>
          <Stack>
            <h3>Tax Rates</h3>
          </Stack>
          <Stack className={addTaxRateClassName}>
            <Stack>
              <Button appearance="primary" icon={<AddRegular />} onClick={createTaxRateComponent}>
                Add New Tax Rate
              </Button>
            </Stack>
          </Stack>
        </Stack>

        {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (fields as any).map((taxRate: ITaxRate, index: number) => {
            const fieldName = `taxRates[${index}]`;
            return (
              <Stack
                key={taxRate.id}
                horizontal
                tokens={{ childrenGap: 10 }}
                horizontalAlign="space-between"
                style={stackStyles}
                className="ms-depth-8"
              >
                <Button
                  appearance="subtle"
                  style={buttonStyles}
                  icon={<DismissRegular color={iconButtonStyles.color} />}
                  title="Remove Tax Rate"
                  aria-label="Remove Tax Rate"
                  onClick={(): void => {
                    remove(index);
                  }}
                />
                <input
                  type="hidden"
                  ref={register()}
                  name={`${fieldName}.id`}
                  defaultValue={taxRate.id}
                />
                <StackColumn minWidth="200px">
                  <Controller
                    name={`${fieldName}.taxType`}
                    control={control}
                    render={({ onChange, onBlur, value, name }): React.ReactElement => (
                      <TaxTypeDropdown
                        label="Tax Type"
                        value={taxRate?.taxType}
                        valueKey="taxType"
                        handleChange={(newValue: string, valueKey: string): void => {
                          setValue(`${fieldName}.taxType`, newValue);
                        }}
                        errors={errors}
                        errorMessage={errors?.taxRates?.[index]?.taxType?.message}
                        required
                      />
                    )}
                    defaultValue={taxRate?.taxType || null}
                    rules={{
                      required: 'Please select a tax type',
                    }}
                  />

                  <Controller
                    label="Description"
                    name={`${fieldName}.taxDescription`}
                    control={control}
                    defaultValue={taxRate?.taxDescription || ''}
                    rules={{
                      maxLength: {
                        value: 250,
                        message: 'Input is greater than the maximum character limit of 250.',
                      },
                    }}
                    render={({ onChange, value, name }): React.ReactElement => (
                      <Field
                        label="Description"
                        validationMessage={errors?.taxRates?.[index]?.taxDescription?.message}
                      >
                        <Input
                          name={name}
                          value={value}
                          onChange={(e, inputData) => onChange(inputData.value)}
                        />
                      </Field>
                    )}
                  />
                </StackColumn>
                <StackColumn minWidth="200px">
                  <Controller
                    name={`${fieldName}.rate`}
                    control={control}
                    render={({ name }): React.ReactElement => (
                      <Field
                        label="Tax Rate"
                        required
                        validationMessage={errors?.taxRates?.[index]?.rate?.message}
                      >
                        <Input
                          name={name}
                          type="number"
                          onBlur={(): void => {
                            if (formState.isSubmitted) {
                              trigger();
                            }
                          }}
                          onChange={(e, inputData): void => {
                            setValue(`${fieldName}.rate`, parseFloat(inputData.value) || null);
                          }}
                          defaultValue={taxRate?.rate?.toString() || ''}
                        />
                      </Field>
                    )}
                    rules={{
                      min: {
                        value: 0,
                        message: 'Tax Rate cannot be less than zero',
                      },
                      required: 'Please provide a Tax Rate',
                    }}
                  />

                  <Controller
                    name={`${fieldName}.effectiveDate`}
                    control={control}
                    defaultValue={taxRate.effectiveDate}
                    render={({ value }): React.ReactElement => (
                      <UtcDatePicker
                        label="Effective Date"
                        isRequired
                        textField={{
                          name: 'effectiveDate',
                          errorMessage: errors?.taxRates?.[index]?.effectiveDate?.message,
                        }}
                        value={value ? momentUtc(value) : null}
                        onSelectDate={(utcDateString: string): void => {
                          setValue(`${fieldName}.effectiveDate`, utcDateString);
                        }}
                      />
                    )}
                    rules={{
                      required: 'Please provide a new date',
                    }}
                  />
                </StackColumn>
              </Stack>
            );
          })
        }
      </Stack>
    </FluentProvider>
  );
};

export default ComplianceRequirementForm;
